<template>
  <div
    data-testid="chat-bot"
    class="chatbot-container"
    :class="{ 'suggestions-open': suggestionsVisible }"
  >
    <div v-if="isTokenLoading" data-testid="chat-bot__loading-container" class="loading-container">
      <v-progress-circular data-testid="chat-bot__loading-spinner" indeterminate color="primary"></v-progress-circular>
      <div data-testid="chat-bot__loading-text" class="mt-2">{{ UI_TEXT.LOADING_CHAT }}</div>
    </div>
    <div v-else-if="connectionError || tokenError" data-testid="chat-bot__error-alert" class="error-message">
      <span data-testid="chat-bot__error-message">{{ connectionError || tokenError }}</span>
      <v-btn data-testid="chat-bot__retry-button" color="primary" @click="retryConnection">{{ UI_TEXT.RETRY_BUTTON }}</v-btn>
    </div>
    <div class="chatbot-content-wrapper" v-show="!connectionError && !tokenError && !isTokenLoading">
      <!-- Welcome Screen - shown before first message -->
      <transition name="welcome-fade">
        <div v-if="showWelcomeScreen" data-testid="chat-bot__welcome-screen" class="welcome-screen">
          <div data-testid="chat-bot__welcome-main-content" class="welcome-main-content">
            <div data-testid="chat-bot__welcome-content" class="welcome-content">
              <h1 data-testid="chat-bot__welcome-title" class="welcome-title">{{ UI_TEXT.WELCOME_TITLE }}</h1>
              <p data-testid="chat-bot__welcome-subtitle" class="welcome-subtitle">{{ UI_TEXT.WELCOME_SUBTITLE }}</p>
            </div>
            
            <div data-testid="chat-bot__welcome-input-container" class="welcome-input-container">
              <input
                data-testid="chat-bot__welcome-input"
                v-model="welcomeMessage"
                type="text"
                class="welcome-input text-transform-none"
                :placeholder="UI_TEXT.WELCOME_INPUT_PLACEHOLDER"
                @keyup.enter="sendWelcomeMessage"
              />
              <button
                data-testid="chat-bot__welcome-send-button"
                class="welcome-send-button"
                @click="sendWelcomeMessage"
                :disabled="!welcomeMessage.trim()"
              >
                <img src="/img/icons/send-horizontal.svg" alt="Send" class="welcome-send-icon" />
              </button>
            </div>
            <!-- <chat-context-selector :standalone="true" /> -->
          </div>
          
          <!-- Side peek suggestions on welcome screen -->
          <chat-suggestions 
            data-testid="chat-bot__welcome-suggestions"
            :is-visible="suggestionsVisible" 
            :suggestions="suggestions" 
            @open="openSuggestions"
            @close="closeSuggestions" 
            @select="populateWelcomeInput"
          />
        </div>
      </transition>
      
      <!-- Main Chat Interface -->
      <transition name="chat-slide">
        <div v-show="!showWelcomeScreen" data-testid="chat-bot__chat-wrapper" class="chat-wrapper">
          <div class="chat-main-row">
            <div data-testid="chat-bot__webchat-container" :id="DOM_SELECTORS.WEBCHAT_CONTAINER" class="webchat-main"></div>
            <chat-suggestions
              data-testid="chat-bot__suggestions"
              :is-visible="suggestionsVisible"
              :suggestions="suggestions"
              @open="openSuggestions"
              @close="closeSuggestions"
              @select="selectSuggestion"
            />
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
// Import utils and constants
import { createDirectLineWithHeaders, loadWebChatScript } from '../utils';
import { 
  WEBCHAT_STYLE_OPTIONS, 
  CONNECTION_STATUSES, 
  LOCALIZED_STRINGS, 
  UI_TEXT,
  ERROR_MESSAGES,
  CONSOLE_MESSAGES,
  DOM_SELECTORS,
  DOM_ATTRIBUTES,
  WEBCHAT_CONFIG,
  OBSERVER_CONFIG,
  ROUTE_PATHS,
  TIMINGS,
  ERROR_CODES,
  FLUX_ALIASES,
  FBC_ALIASES,
} from '../utils/constants';
import { mapState, mapActions, mapGetters } from 'vuex';
import ChatSuggestions from '../components/ChatSuggestions.vue';

export default {
  name: UI_TEXT.COMPONENT_NAME,
  components: {
    ChatSuggestions,
  },
  data() {
    return {
      right: UI_TEXT.RIGHT_PANEL,
      loading: false,
      errors: {},
      directLine: null, // Initialize directLine property
      suggestionsVisible: true,
      // Default to hidden and set explicitly in mounted based on connection status.
      showWelcomeScreen: false,
      welcomeMessage: '', // Welcome screen input message
      pendingWelcomeMessage: null, // Pending message to send after transition
      // Make constants available in template
      UI_TEXT,
      DOM_SELECTORS,
      DOM_ATTRIBUTES
    };
  },
  computed: {
    ...mapState({
      directLineToken: state => state.chatbot.directLineToken,
      connectionError: state => state.chatbot.connectionError,
      isTokenLoading: state => state.chatbot.isTokenLoading,
      tokenError: state => state.chatbot.tokenError,
      selectedProjectId: state => state.chatbot.project,
      clientDetails: state => state.clientDetails
    }),
    ...mapGetters('chatbot', ['toolsCatalog', 'isConnected']),
    suggestions() {
      const tools = Array.isArray(this.toolsCatalog) ? this.toolsCatalog : [];
      const activeApplication = this.getActiveApplication();
      const hasFinancialCloseAccess = this.clientDetails?.hasFinancialCloseAccess !== false;
      const hasVarianceAnalysisAccess = this.clientDetails?.hasVarianceAnalysisAccess !== false;

      return tools.filter(tool => {
        const app = this.normalizeApplication(tool?.application);

        if (app === 'common') return true;
        if (app === 'fbc') return activeApplication === 'fbc' && hasFinancialCloseAccess;
        if (app === 'flux') return activeApplication === 'flux' && hasVarianceAnalysisAccess;

        return false;
      });
    },
    userId() {
      return this.$store.state.user?.id;
    }
  },
  async mounted() {
    // If DirectLine is already connected (status 2), skip re-fetching token/catalog and reinitializing WebChat.
    const isAlreadyConnected = this.$store.state.chatbot.connectionStatus === CONNECTION_STATUSES.CONNECTED;
    this.showWelcomeScreen = !isAlreadyConnected;
    await this.fetchToken();
    await this.fetchToolsCatalog();
    this.initChat();
    // Add a MutationObserver to inject our suggestions button after WebChat is loaded
    this.setupWebchatObserver();
  },
  methods: {
        updateWebchatSendButtonState(sendButton, webchatInput) {
          if (!sendButton || !webchatInput) return;

          const hasText = Boolean((webchatInput.value || '').trim());

          if (hasText) {
            sendButton.classList.remove('webchat__send-button--disabled');
            sendButton.removeAttribute('disabled');
            sendButton.setAttribute('aria-disabled', 'false');
            sendButton.style.pointerEvents = '';
            return;
          }

          sendButton.classList.add('webchat__send-button--disabled');
          sendButton.setAttribute('disabled', 'disabled');
          sendButton.setAttribute('aria-disabled', 'true');
          sendButton.style.pointerEvents = 'none';
        },

        bindWebchatSendButtonState(sendButton, webchatInput) {
          if (!sendButton || !webchatInput || sendButton.dataset.stateBound === 'true') return;

          const syncState = () => this.updateWebchatSendButtonState(sendButton, webchatInput);

          // Keep native behavior but enforce initial/visual disabled state.
          webchatInput.addEventListener('input', syncState);
          webchatInput.addEventListener('keyup', syncState);
          webchatInput.addEventListener('change', syncState);

          sendButton.dataset.stateBound = 'true';
          syncState();
        },

    ...mapActions({
      setConnectionError: 'chatbot/setConnectionError',
      updateConnectionStatus: 'chatbot/updateConnectionStatus',
      fetchDirectLineToken: 'chatbot/fetchDirectLineToken',
      fetchToolsCatalog: 'chatbot/fetchToolsCatalog',
      logoutUser: 'chatbot/logout'
    }),

    normalizeApplication(value) {
      const normalized = (value || 'common').toString().trim().toLowerCase();

      if (FLUX_ALIASES.includes(normalized)) return 'flux';
      if (FBC_ALIASES.includes(normalized)) return 'fbc';

      return normalized || 'common';
    },

    isFluxCycleType(value) {
      const normalized = (value || '').toString().trim().toLowerCase();
      return FLUX_ALIASES.some(token => normalized.includes(token));
    },
    
    getActiveApplication() {
      const projectList = this.$store.getters['project/projectList'] || [];
      const selectedProject = projectList.find(project => project?._id === this.selectedProjectId);
      const cycleTypeCandidates = [
        selectedProject?.cycle_type,
        selectedProject?.cycleType,
        selectedProject?.cycleTypeName,
        selectedProject?.cycle_type_name,
        selectedProject?.application,
        selectedProject?.module,
      ];

      const hasFluxCycleType = cycleTypeCandidates.some(value => this.isFluxCycleType(value));

      if (hasFluxCycleType) {
        return 'flux';
      }

      const routePath = (this.$route?.path || '').toLowerCase();

      if (routePath.includes('/varianceanalysis') || routePath.includes('/flux')) {
        return 'flux';
      }

      return 'fbc';
    },

    async fetchToken() {
      try {
        const result = await this.fetchDirectLineToken();
        if (result.status !== 'success') {
          this.setConnectionError(ERROR_MESSAGES.FETCH_TOKEN_FAILED);
        }
      } catch (error) {
        this.setConnectionError(ERROR_MESSAGES.INITIALIZE_CHAT_ERROR);
        console.error(CONSOLE_MESSAGES.FETCH_TOKEN_ERROR, error);
      }
    },
    
    async retryConnection() {
      this.setConnectionError(null);
      await this.fetchToken();
      this.initChat();
    },

    toggleSuggestions() {
      this.suggestionsVisible = !this.suggestionsVisible;
    },

    openSuggestions() {
      this.suggestionsVisible = true;
    },

    closeSuggestions() {
      this.suggestionsVisible = false;
    },

    /**
     * Send the welcome message and transition to chat view
     */
    sendWelcomeMessage() {
      if (!this.welcomeMessage.trim()) return;
      
      const message = this.welcomeMessage;
      this.welcomeMessage = '';
      
      // Store the pending message to be sent once WebChat is ready
      this.pendingWelcomeMessage = message;
      
      // Hide welcome screen with transition
      this.showWelcomeScreen = false;
      
      // Wait for WebChat to be ready, then populate and send the message
      this.$nextTick(() => {
        this.sendPendingWelcomeMessage();
      });
    },
    
    /**
     * Attempt to send the pending welcome message directly through DirectLine
     * This avoids WebChat adding ClientCapabilities entities that can cause issues
     */
    sendPendingWelcomeMessage() {
      if (!this.pendingWelcomeMessage) return;
      
      const attemptSend = (retries = 0) => {
        // Check if DirectLine is ready and connected
        if (this.directLine && this.isConnected) {
          const messageToSend = this.pendingWelcomeMessage;
          this.pendingWelcomeMessage = null;
          
          // Build activity matching WebChat's format (without ClientCapabilities entities)
          const activity = {
            type: 'message',
            text: messageToSend,
            textFormat: 'plain',
            channelId: 'webchat',
            from: {
              id: this.userId || '',
              name: '',
              role: 'user'
            },
            locale: WEBCHAT_CONFIG.LOCALE,
            localTimestamp: new Date().toISOString(),
            localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          };
          
          // Send directly through DirectLine (postActivity adds headers/params via wrapper)
          this.directLine.postActivity(activity).subscribe(
            id => {
              console.log('[ChatBot]: Welcome message sent directly via DirectLine:', messageToSend, 'Activity ID:', id);
            },
            error => {
              console.error('[ChatBot]: Failed to send welcome message:', error);
            }
          );
        } else if (retries < 20) {
          // Retry after a short delay if DirectLine isn't ready yet
          setTimeout(() => attemptSend(retries + 1), 300);
        } else {
          console.error('[ChatBot]: Failed to send welcome message - DirectLine not ready after retries');
          this.pendingWelcomeMessage = null;
        }
      };
      
      // Start attempting to send after a small delay for transition
      setTimeout(() => attemptSend(), 500);
    },

    /**
     * Handle suggestion selection from welcome screen - populates input box
     */
    populateWelcomeInput(suggestion) {
      if (!suggestion?.prompt) return;
      
      // Populate the welcome input with the suggestion prompt
      this.welcomeMessage = suggestion.prompt;
      
      // Close the suggestions panel
      this.closeSuggestions();
    },

    selectSuggestion(suggestion) {
      if (window.WebChat && this.directLine) {
        // Find the input element and append the suggestion to existing text
        const inputElement = document.querySelector(DOM_SELECTORS.WEBCHAT_INPUT);
        if (inputElement && suggestion.prompt) {
          // Get current value and append the suggestion
          const currentValue = inputElement.value || '';
          const newValue = currentValue ? `${currentValue} ${suggestion?.prompt || ''}` : (suggestion?.prompt || '');
          
          // For React-controlled inputs, use the native setter from element's prototype chain
          // This works for any input type (INPUT, TEXTAREA, or future element types)
          const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            Object.getPrototypeOf(inputElement),
            'value'
          ).set;
          nativeInputValueSetter.call(inputElement, newValue);
          
          // Dispatch both input and change events to trigger React's event handlers
          const inputEvent = new Event(DOM_ATTRIBUTES.INPUT_EVENT_TYPE, { bubbles: DOM_ATTRIBUTES.INPUT_EVENT_BUBBLES });
          inputElement.dispatchEvent(inputEvent);
          
          const changeEvent = new Event('change', { bubbles: true });
          inputElement.dispatchEvent(changeEvent);
          
          inputElement.focus();

          // Close suggestions panel after setting the text
          this.closeSuggestions();
        }
      } else {
        console.warn(CONSOLE_MESSAGES.WEBCHAT_NOT_INITIALIZED);
        // Optionally show a notification to the user
        if (suggestion && suggestion.prompt) {
          // Store the suggestion to try setting when webchat is ready
          this.pendingSuggestion = suggestion.prompt;
        }
      }
    },
    
    /**
     * Handle session expiration by showing toastr message and logging out
     */
    async handleSessionExpired() {
      this.$store.commit('sbError', 'Session expired', { root: true });
      // Add delay to allow user to see the message before logout
      setTimeout(async () => {
        await this.performLogout();
      }, TIMINGS.SESSION_EXPIRED_DELAY);
    },
    
    /**
     * Logout user and redirect to home
     * Uses store action for proper state management and error handling
     */
    async performLogout() {
      await this.logoutUser();
      // Always redirect to home after logout attempt
      this.$router.push({ path: ROUTE_PATHS.HOME });
    },
    
    initChat() {
      loadWebChatScript(() => {
        this.initializeWebChat();
      });
    },
    
    initializeWebChat() {
      if (window.WebChat && this.directLineToken) {
        try {
          // Create a custom DirectLine object with middleware for adding headers
          const directLine = createDirectLineWithHeaders(
            this.directLineToken, 
            this.$tokenUtils.getSecureToken, 
            this.$store
          );
          
          // Store directLine reference for later use with suggestions
          this.directLine = directLine;
          
          // Monitor connection status changes
          directLine.connectionStatus$.subscribe(
            connectionStatus => {
              this.updateConnectionStatus(connectionStatus);
              
              if (connectionStatus === CONNECTION_STATUSES.CONNECTED) {
                this.setConnectionError(null);
              } else if (connectionStatus === CONNECTION_STATUSES.FAILED) {
                this.setConnectionError(ERROR_MESSAGES.CONNECTION_FAILED);
              }
            },
            error => {
              console.error(CONSOLE_MESSAGES.DIRECTLINE_CONNECTION_ERROR, error);
              this.setConnectionError(ERROR_MESSAGES.CONNECTION_ERROR_PREFIX + error.message);
            }
          );
          
          // Monitor incoming activities for session expiration
          directLine.activity$.subscribe(activity => {
            // Check for widget token expiration in channelData
            if (activity.channelData?.error?.code === ERROR_CODES.WIDGET_TOKEN_EXPIRED) {
              this.handleSessionExpired();
            }
          });
          
          window.WebChat.renderWebChat({
            directLine: directLine,
            userID: this.userId,
            locale: WEBCHAT_CONFIG.LOCALE,
            // Add a custom placeholder text
            overrideLocalizedStrings: (strings) => ({
              ...strings,
              ...(LOCALIZED_STRINGS.TEXT_INPUT_PLACEHOLDER && { TEXT_INPUT_PLACEHOLDER: LOCALIZED_STRINGS.TEXT_INPUT_PLACEHOLDER })
            }),
            styleOptions: { ...WEBCHAT_STYLE_OPTIONS, ...this.$store.getters['chatbot/computedStyleOptions'] }
          }, document.getElementById(DOM_SELECTORS.WEBCHAT_CONTAINER));
        } catch (error) {
          console.error(CONSOLE_MESSAGES.INITIALIZE_WEBCHAT_ERROR, error);
          this.setConnectionError(ERROR_MESSAGES.INITIALIZE_WEBCHAT_ERROR + error.message);
        }
      } else {
        this.setConnectionError(ERROR_MESSAGES.WEBCHAT_NOT_LOADED);
      }
    },
    
    getWebchatContainer() {
      return document.getElementById(DOM_SELECTORS.WEBCHAT_CONTAINER);
    },

    styleBotAvatars(webchatContainer) {
      const root = webchatContainer || this.getWebchatContainer();
      if (!root) return;

      root.querySelectorAll('img[src*="AskNoa.svg"]').forEach((img) => {
        const avatar = img.parentElement;
        if (avatar && !avatar.classList.contains('chatbot-bot-avatar')) {
          avatar.classList.add('chatbot-bot-avatar');
        }
      });
    },

    setupWebchatObserver() {
      const webchatContainer = this.getWebchatContainer();
      if (!webchatContainer) return;

      const observer = new MutationObserver(() => {
        this.styleBotAvatars(webchatContainer);

        const sendBoxMain = webchatContainer.querySelector(DOM_SELECTORS.SEND_BOX_MAIN);
        const sendButton = webchatContainer.querySelector(DOM_SELECTORS.SEND_BUTTON);
        const webchatInput = webchatContainer.querySelector(DOM_SELECTORS.WEBCHAT_INPUT);

        if (webchatInput && !webchatInput.classList.contains('text-transform-none')) {
          webchatInput.classList.add('text-transform-none');
        }

        if (sendButton && webchatInput) {
          this.bindWebchatSendButtonState(sendButton, webchatInput);
        }

        if (sendBoxMain && sendButton && !webchatContainer.querySelector(DOM_SELECTORS.SUGGESTIONS_BUTTON)) {
          const suggestionsButton = document.createElement('div');
          suggestionsButton.className = DOM_ATTRIBUTES.SUGGESTIONS_BUTTON_CLASS;
          suggestionsButton.addEventListener('click', () => this.toggleSuggestions());
          sendButton.parentNode.insertBefore(suggestionsButton, sendButton);
        }
      });

      this._webchatObserver = observer;
      observer.observe(webchatContainer, {
        childList: OBSERVER_CONFIG.CHILD_LIST,
        subtree: OBSERVER_CONFIG.SUBTREE,
      });
    }
  },
  
  /**
   * Reset component-specific data to prevent stale state on re-entry
   */
  beforeDestroy() {
    if (this._webchatObserver) {
      this._webchatObserver.disconnect();
      this._webchatObserver = null;
    }
    this.suggestionsVisible = true;
    this.showWelcomeScreen = this.$store.state.chatbot.connectionStatus !== CONNECTION_STATUSES.CONNECTED;
    this.welcomeMessage = '';
    this.pendingWelcomeMessage = null;
  }
};
</script>

<style>
/* Global import: WebChat injects DOM without Vue scoped data-v attributes */
@import '../styles/ChatBot.css';
</style>
